import React, {FC, useCallback, useEffect, useMemo,} from "react";
import {INavLink, INavLinkGroup, Nav, ProgressIndicator, Stack, StackItem,} from "@fluentui/react";
import {Outlet, useNavigate} from "react-router-dom";
import axios from "axios";
import {Depths} from "@fluentui/theme";
import {WikiEntry, WikiEntryCategory} from "../API";
import {AppRoute} from "../AppRoutes";
import {useLocalStorage} from "../helpers/useLocalStorage";

export const WikiLayout: FC = () => {
    const expandedGroupAutomationIds = "expandedGroupAutomationIds";
    const categoriesLocalStorageKey = "wikiCategoriesLocalStorageKey";
    const [expandedLinks, setExpandedLinks] = useLocalStorage<string[]>(
        expandedGroupAutomationIds,
        [],
    );
    const [categories, setCategories] = useLocalStorage<
        WikiEntryCategory[] | undefined
    >(categoriesLocalStorageKey, undefined);
    const navigate = useNavigate();

    useEffect(() => {
        axios
            .post("/WikiEntryCategoryPublic/GetNonEmptyCategoriesWithHierarchy")
            .then((res) => {
                setCategories(res.data);
            });
    }, []);

    const makeEntryLinksOf = useCallback(
        (categories: WikiEntry[]): INavLink[] => {
            const ret: INavLink[] = [];

            for (const entry of categories) {
                const navLink: INavLink = {
                    name: entry.title,
                    url: AppRoute.WIKI_PUBLIC({entryId: entry.id}),
                    automationId: entry.id.toString(),
                    iconProps: {iconName: "PageSolid"},
                };

                ret.push(navLink);
            }

            return ret;
        },
        [],
    );

    const makeCategoryLinksOf = useCallback(
        (categories: WikiEntryCategory[]): INavLink[] => {
            const ret: INavLink[] = [];

            for (const cat of categories) {
                const navLink: INavLink = {
                    links: [
                        ...makeCategoryLinksOf(cat.children),
                        ...makeEntryLinksOf(cat.wikiEntries),
                    ],
                    name: cat.name,
                    url: AppRoute.WIKI_PUBLIC({categoryId: cat.id}),
                    automationId: cat.id.toString(),
                    isExpanded: expandedLinks.includes(cat.id.toString()),
                    // iconProps: {iconName: "FolderHorizontal"},
                };

                ret.push(navLink);
            }

            return ret;
        },
        [expandedLinks],
    );

    const makeNavsOf = useCallback(
        (categories: WikiEntryCategory[]): INavLinkGroup[] => {
            const ret: INavLinkGroup[] = [];

            for (const cat of categories) {
                const navGroup: INavLinkGroup = {
                    links: [
                        ...makeCategoryLinksOf(cat.children),
                        ...makeEntryLinksOf(cat.wikiEntries),
                    ],
                    name: cat.name,
                    automationId: cat.id.toString(),
                };

                ret.push(navGroup);
            }

            return ret;
        },
        [expandedLinks],
    );

    const navGroups = useMemo(() => {
        if (categories) {
            return makeNavsOf(categories);
        }
    }, [categories, expandedLinks]);

    const onLinkExpandClick = useCallback(
        (ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
            if (item) {
                if (item.isExpanded) {
                    setExpandedLinks(
                        expandedLinks.filter((x) => x !== item.automationId),
                    );
                } else {
                    setExpandedLinks([...expandedLinks, item.automationId!]);
                }
            }
        },
        [expandedGroupAutomationIds, expandedLinks],
    );

    const onLinkClick = useCallback(
        (ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
            if (item?.url) {
                item.isExpanded = true;
                ev?.preventDefault();
                navigate(item.url);
            }
        },
        [navigate],
    );

    return (
        <Stack tokens={{childrenGap: 10}} horizontal style={{height: "100%"}}>
            <StackItem
                style={{width: 300, height: "100%", boxShadow: Depths.depth4}}
            >
                {navGroups ? (
                    <Nav
                        styles={{root: {height: "100vh"}}}
                        groups={navGroups}
                        onLinkClick={onLinkClick}
                        onLinkExpandClick={onLinkExpandClick}
                    />
                ) : (
                    <ProgressIndicator/>
                )}
            </StackItem>
            <StackItem grow={1} style={{height: "100vh", overflowY: "scroll"}}>
                <Outlet/>
            </StackItem>
        </Stack>
    );
};
